/**
 * Invoice Service
 * Manages invoice generation, management, and export
 */

import { supabase } from './supabase'
import { exportToCSV, exportToExcel } from './exportService'

/**
 * Generate invoice number
 */
const generateInvoiceNumber = async () => {
  try {
    const { data, error } = await supabase.rpc('generate_invoice_number')
    if (error) throw error
    return data
  } catch (error) {
    // Fallback if function doesn't exist
    const year = new Date().getFullYear()
    const random = Math.random().toString(36).substr(2, 6).toUpperCase()
    return `INV-${year}-${random}`
  }
}

/**
 * Create an invoice
 */
export const createInvoice = async (organizationId, invoiceData) => {
  try {
    const invoiceNumber = await generateInvoiceNumber()

    const { data, error } = await supabase
      .from('invoices')
      .insert({
        organization_id: organizationId,
        billing_cycle_id: invoiceData.billingCycleId,
        invoice_number: invoiceNumber,
        invoice_date: invoiceData.invoiceDate || new Date().toISOString(),
        due_date: invoiceData.dueDate,
        subtotal: invoiceData.subtotal || 0,
        tax_amount: invoiceData.taxAmount || 0,
        discount_amount: invoiceData.discountAmount || 0,
        total_amount: invoiceData.totalAmount,
        currency: invoiceData.currency || 'USD',
        line_items: invoiceData.lineItems || [],
        notes: invoiceData.notes,
        payment_terms: invoiceData.paymentTerms || 'Net 30',
        billing_address: invoiceData.billingAddress,
        status: invoiceData.status || 'draft',
      })
      .select()
      .single()

    if (error) throw error
    return { success: true, data }
  } catch (error) {
    console.error('Error creating invoice:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Generate invoice from billing cycle
 */
export const generateInvoiceFromBillingCycle = async (organizationId, billingCycleId) => {
  try {
    // Get billing cycle
    const { data: cycle, error: cycleError } = await supabase
      .from('billing_cycles')
      .select('*')
      .eq('id', billingCycleId)
      .eq('organization_id', organizationId)
      .single()

    if (cycleError) throw cycleError

    // Get organization
    const { data: org, error: orgError } = await supabase
      .from('organizations')
      .select('*')
      .eq('id', organizationId)
      .single()

    if (orgError) throw orgError

    // Get subscription details
    let subscription = null
    if (cycle.subscription_id) {
      const { data: sub, error: subError } = await supabase
        .from('organization_subscriptions')
        .select('*, subscription_tiers(name, price_monthly, price_yearly)')
        .eq('id', cycle.subscription_id)
        .single()

      if (!subError && sub) {
        subscription = sub
      }
    }

    // Calculate due date (30 days from invoice date)
    const dueDate = new Date()
    dueDate.setDate(dueDate.getDate() + 30)

    // Create line items
    const lineItems = [
      {
        description: subscription
          ? `${subscription.subscription_tiers?.name || 'Subscription'} - ${cycle.billing_cycle_type}`
          : `Billing Cycle - ${cycle.billing_cycle_type}`,
        quantity: 1,
        unit_price: cycle.amount,
        amount: cycle.amount,
      },
    ]

    // Create invoice
    const invoiceData = {
      billingCycleId: billingCycleId,
      invoiceDate: new Date().toISOString(),
      dueDate: dueDate.toISOString(),
      subtotal: cycle.amount,
      taxAmount: 0,
      discountAmount: 0,
      totalAmount: cycle.amount,
      currency: cycle.currency || 'USD',
      lineItems,
      paymentTerms: 'Net 30',
      status: 'draft',
    }

    return await createInvoice(organizationId, invoiceData)
  } catch (error) {
    console.error('Error generating invoice from billing cycle:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get invoices for organization
 */
export const getInvoices = async (organizationId, filters = {}) => {
  try {
    let query = supabase
      .from('invoices')
      .select('*, billing_cycles(cycle_start_date, cycle_end_date, billing_cycle_type)')
      .eq('organization_id', organizationId)
      .order('invoice_date', { ascending: false })

    if (filters.status) {
      query = query.eq('status', filters.status)
    }

    if (filters.startDate) {
      query = query.gte('invoice_date', filters.startDate)
    }

    if (filters.endDate) {
      query = query.lte('invoice_date', filters.endDate)
    }

    const { data, error } = await query

    if (error) throw error
    return { success: true, data: data || [] }
  } catch (error) {
    console.error('Error getting invoices:', error)
    return { success: false, error: error.message, data: [] }
  }
}

/**
 * Get invoice by ID
 */
export const getInvoice = async (invoiceId, organizationId) => {
  try {
    const { data, error } = await supabase
      .from('invoices')
      .select('*, billing_cycles(*), organizations(name)')
      .eq('id', invoiceId)
      .eq('organization_id', organizationId)
      .single()

    if (error) throw error
    return { success: true, data }
  } catch (error) {
    console.error('Error getting invoice:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Update invoice status
 */
export const updateInvoiceStatus = async (invoiceId, organizationId, status) => {
  try {
    const updateData = {
      status,
      updated_at: new Date().toISOString(),
    }

    if (status === 'paid') {
      updateData.paid_date = new Date().toISOString()
    }

    const { data, error } = await supabase
      .from('invoices')
      .update(updateData)
      .eq('id', invoiceId)
      .eq('organization_id', organizationId)
      .select()
      .single()

    if (error) throw error
    return { success: true, data }
  } catch (error) {
    console.error('Error updating invoice status:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Mark invoice as sent
 */
export const markInvoiceAsSent = async (invoiceId, organizationId) => {
  return updateInvoiceStatus(invoiceId, organizationId, 'sent')
}

/**
 * Mark invoice as paid
 */
export const markInvoiceAsPaid = async (invoiceId, organizationId, paymentTransactionId = null) => {
  try {
    const updateData = {
      status: 'paid',
      paid_date: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }

    if (paymentTransactionId) {
      updateData.payment_transaction_id = paymentTransactionId
    }

    const { data, error } = await supabase
      .from('invoices')
      .update(updateData)
      .eq('id', invoiceId)
      .eq('organization_id', organizationId)
      .select()
      .single()

    if (error) throw error

    // Update related billing cycle if exists
    if (data.billing_cycle_id) {
      await supabase
        .from('billing_cycles')
        .update({
          status: 'paid',
          paid_date: new Date().toISOString(),
        })
        .eq('id', data.billing_cycle_id)
    }

    return { success: true, data }
  } catch (error) {
    console.error('Error marking invoice as paid:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Export invoice to PDF (placeholder - would need PDF library)
 */
export const exportInvoiceToPDF = async (invoiceId, organizationId) => {
  // TODO: Implement PDF export using a library like jsPDF or react-pdf
  console.warn('PDF export not yet implemented')
  return { success: false, error: 'PDF export not yet implemented' }
}

/**
 * Export invoices to CSV/Excel
 */
export const exportInvoices = async (invoices, format = 'csv') => {
  if (!invoices || invoices.length === 0) {
    alert('No invoices to export')
    return
  }

  const exportData = invoices.map((invoice) => ({
    'Invoice Number': invoice.invoice_number,
    'Invoice Date': new Date(invoice.invoice_date).toLocaleDateString(),
    'Due Date': new Date(invoice.due_date).toLocaleDateString(),
    'Status': invoice.status,
    'Subtotal': invoice.subtotal,
    'Tax': invoice.tax_amount || 0,
    'Discount': invoice.discount_amount || 0,
    'Total': invoice.total_amount,
    'Currency': invoice.currency,
    'Paid Date': invoice.paid_date ? new Date(invoice.paid_date).toLocaleDateString() : '',
  }))

  const timestamp = new Date().toISOString().split('T')[0]
  const filename = `invoices_${timestamp}.${format === 'csv' ? 'csv' : 'xlsx'}`

  if (format === 'csv') {
    exportToCSV(exportData, filename)
  } else {
    await exportToExcel(exportData, filename)
  }
}

/**
 * Get overdue invoices
 */
export const getOverdueInvoices = async (organizationId) => {
  try {
    const { data, error } = await supabase
      .from('invoices')
      .select('*')
      .eq('organization_id', organizationId)
      .in('status', ['sent', 'draft'])
      .lt('due_date', new Date().toISOString())
      .order('due_date', { ascending: true })

    if (error) throw error

    // Update status to overdue
    if (data && data.length > 0) {
      const invoiceIds = data.map((inv) => inv.id)
      await supabase
        .from('invoices')
        .update({ status: 'overdue' })
        .in('id', invoiceIds)
    }

    return { success: true, data: data || [] }
  } catch (error) {
    console.error('Error getting overdue invoices:', error)
    return { success: false, error: error.message, data: [] }
  }
}

export default {
  createInvoice,
  generateInvoiceFromBillingCycle,
  getInvoices,
  getInvoice,
  updateInvoiceStatus,
  markInvoiceAsSent,
  markInvoiceAsPaid,
  exportInvoiceToPDF,
  exportInvoices,
  getOverdueInvoices,
}

